import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import axios from '../../axios/axios';  
import { para_be } from '../../config';
import { ReportsCategory, InventoryCategory, PROPERTY_ROLE, DATETIME_FORMAT } from '../../constants';
import { dateObjFormatter } from '../../utils/date_utils';


const validateVideoLink = async (videoSrc) => {
    try {
        const response = await axios.head(videoSrc, { withCredentials: true, });
        if (response.status === 200) {
            return videoSrc; 
        } else {
            throw new Error('Invalid video link');
        }
    } catch (error) {
        console.error('Video link validation failed:', error);
        return '';  
    }
};

export const fetchReportsData = createAsyncThunk(
    'reports/fetchReportsData',
    async ({ rid }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${para_be}/reports/get_report_data?report_id=${rid}`, {
                withCredentials: true,
            });
            if (response.status >= 400) {
                return rejectWithValue(response.data.msg || 'Error fetching Reports data');
            }
            const result = response.data.result;
            const videoSrc = result[4].replace('.webm', '.mp4');
            const validatedVideoSrc = await validateVideoLink(videoSrc);
            return { ...response.data, validatedVideoSrc, rid };
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Network error');
        }
    }
);

export const purchaseReport = createAsyncThunk(
    'reports/purchaseReport',
    async ({ rid }, { rejectWithValue }) => {
        try {      
            const response = await axios.post(`${para_be}/payments/purchase_report`, { rid }, {
                withCredentials: true,
            });
        
            if (response.status >= 400) {
                return rejectWithValue(response.data.msg || 'Failed to purchase the report.');
            }
            return response.data; 
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Network error');
        }
    }
);

export const checkIsReportPurchased = createAsyncThunk(
    'reports/checkIsReportPurchased',
    async ({ rid }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${para_be}/payments/is_report_purchased`, { rid }, {
                withCredentials: true,
            });
            if (response.status >= 400) {
                return rejectWithValue(response.data.msg || 'Failed to check report purchase status.');
            }
            return response.data.data; 
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Network error');
        }
    }
);

const filterData = (dataKey, orderedData, selectedCategories, checkboxStates, items, isPreviewMode, centralizedStates, selectedCentralizedStates=null) => (
    (dataKey === ReportsCategory.INVENTORY || dataKey === ReportsCategory.CENTRALIZED) ? (
        orderedData[dataKey].filter(
            (item) => (
                !selectedCategories || selectedCategories.length === 0 || selectedCategories.includes(item.category) || selectedCategories.includes(item?.room) || 
                items.includes(item.object_id) 
            )
        ).filter(
            (item) => (
                dataKey !== ReportsCategory.CENTRALIZED ||
                !selectedCentralizedStates || selectedCentralizedStates.length === 0 || 
                selectedCentralizedStates.includes(centralizedStates?.[`${item.room}-${item.classifiedCategory}`]) 
            )
        ).filter((item) => !isPreviewMode || checkboxStates?.[dataKey]?.[item.object_id])
    ) : orderedData[dataKey].filter((item) => !isPreviewMode || checkboxStates?.[dataKey]?.[item.object_id])
);

export const EDIT_STATE = {
    START : "start",
    END: "end"
};

const defaultguestData = {
    "check-in": "",
    checkout: "",
    deposit: "",
    email: "",
    mobile: "",
    name: "",
    renter_id: "",
    tenancy_id: "",
    tenant_id: ""
};

const initialState = {
    data: [],
    loading: false,
    error: null,
    purchasedLoader: false, 
    isEmptyState: false,
    header: '',
    date: '',
    report_id: '',
    videoSrc: '',
    reportData: {},
    orderedData: {},
    filteredData: null,
    tabView: ReportsCategory.INVENTORY,
    pid: '',
    kitchenView: {},
    meterview: {},
    guestData: {},
    selectedCategories: null,
    selectedCentralizedStates: null,
    inspectionType: null,
    hasBaseline: false,
    inventoryCategories: [],
    availableClassifications: [],
    classifiedCategories: {},
    classifiedMetadata: {},
    centralizedStates: {},
    centralizedShowComments: {},
    collapsedStates: [],
    roomsCategories: [],
    roomsData: [],
    mediaData: {},
    inspectionMediaData: {},
    checkboxStates: {},
    itemMetadata: {},
    includeVideoInExport: false,
    excludeCostFromExport: false,
    editExportMode: false,
    finishExportMode: false,
    previewMode: false,
    centralizedView: false,
    addReportItemModal: false,
    editInspectionTypeModal: false,
    // addReportItemModal: {
    //     room: 'Laundry Room',
    // },
    imageViewerModal: false,
    auditoryNotesViewerModal: false,
    videoPlayerModal: false,
    zoomOutView: false,
    selectedImageIndex: null,
    selectedNoteIndex: null,
    selectedNoteItems: [],
    saveEditData: null,
    linkModalOpen: { open: false },
    purchaseStatus: null, 
    receiptUrl: null,
    isLocked: false,
    commentModes: {},
    management: {},
    auditoryNotes: [
        // {
        //     'room': "Outdoor", 
        //     'media': [
        //         "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_042520250508491745557729?idx=28_orig_size&lt=1",
        //         "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_042520250508491745557729?idx=29_orig_size&lt=1",
        //     ],
        //     'object_id': "NOTE_0",
        //     'issue_type': "Issue",
        //     'text': "The driveway will require some cleaning and maintenance to remove dirt and debris.",
        //     'additional_properties': {}
        // },
        // {
        //     'room': "Front Porch", 
        //     'media': [
        //         "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_042520250508491745557729?idx=34_orig_size&lt=1",
        //         "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_042520250508491745557729?idx=35_orig_size&lt=1",
        //     ],
        //     'object_id': "NOTE_1",
        //     'issue_type': "Issue",
        //     'text': "The front door is missing the patio screen door.",
        //     'additional_properties': {}
        // },
        // {
        //     'room': "Front Porch", 
        //     'media': [
        //         "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_042520250508491745557729?idx=36_orig_size&lt=1",
        //         "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_042520250508491745557729?idx=38_orig_size&lt=1",
        //     ],
        //     'object_id': "NOTE_2",
        //     'issue_type': "Issue",
        //     'text': "There are significant signs of wear and tear and structural damage to the front porch, on the floor, walls, and ceiling.",
        //     'additional_properties': {}
        // },
        // {
        //     'room': "Front Porch", 
        //     'media': [
        //         "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_042520250508491745557729?idx=41_orig_size&lt=1",
        //         "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_042520250508491745557729?idx=42_orig_size&lt=1",
        //     ],
        //     'object_id': "NOTE_3",
        //     'issue_type': "Note",
        //     'text': "Please note that we need to set up a ring door bell before the tenant moves in.",
        //     'additional_properties': {}
        // },
    ],
    exportReport: {
        propertyRole: PROPERTY_ROLE.RESIDENT,
        exportName: '',
        allExportReports: [],
        currentExportReport: null,
        customText: '',
        selectedCurrency: "usd",
        existence: true
    },
    fetchExportReportLoding: false,
};

const reportsSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        updateFilteredData: (state, action) => {
            const dataKey = action.payload;
            const items = (state.selectedCategories || []).reduce(
                (acc, category) => [
                    ...acc, 
                    ...((state.roomsData || []).length === 0 ? state.inventoryCategories : state.roomsCategories[dataKey])[category]
                ], []
            );
            state.filteredData = filterData(
                dataKey, state.orderedData, state.selectedCategories, state.checkboxStates, items, 
                state.previewMode, state.centralizedStates, state.selectedCentralizedStates
            );
            state.tabView = dataKey;
        },
        updateViewType(state, action) {
            const isCentralized = action.payload === ReportsCategory.CENTRALIZED;
            state.centralizedView = isCentralized;
            if (isCentralized) {
                state.tabView = ReportsCategory.CENTRALIZED;
            } else {
                state.tabView = ReportsCategory.INVENTORY;
            }
        },
        toggleEditMode(state,action) {
            if (EDIT_STATE.START === action.payload) {
                state.editExportMode = true;
                state.finishExportMode = false;
            } else {
                state.finishExportMode = true;
            }
        },
        togglePropertyRole: (state, action) => {
            state.exportReport.propertyRole = action.payload;
        },
        updateCollapsedStates: (state, action) => {
            const { collapse, section } = action.payload;
            if (collapse) {
                state.collapsedStates.push(section);
            } else {
                state.collapsedStates = state.collapsedStates.filter((s) => s !== section);
            }
        },
        selectFrameARI: (state, action) => {
            const { img, imgIndex, frameNum } = action.payload;
            state.addReportItemModal = {...state.addReportItemModal, selectedFrame: img, imgIndex, frameNum};
        },
        setRectARI: (state, action) => {
            const { rect } = action.payload;
            state.addReportItemModal = {...state.addReportItemModal, rect: rect};
        },
        onNewReportItem: (state, action) => {
            const { object_id, object_data } = action.payload;
            const { room, classifiedCategory } = object_data;
            // Update orderedData, classifiedCategories, roomsCategories, availableClassifications
            const newOrderedData = state.orderedData;
            const newClassifiedCategories = state.classifiedCategories;
            const newRoomsCategories = state.roomsCategories;
            const newAvailableClassifications = state.availableClassifications;
            const newCentralizedStates = state.centralizedStates;

            newOrderedData[ReportsCategory.CENTRALIZED].push({
                ...object_data, 
                object_id, 
                subject: object_data.label, 
                issue_type: object_data.class >= 3000 ? "Defect" : "Inventory", 
                media: `https://aiv2.paraspot.ai/cva/v2/getObjectFromScan?src_id=${state.report_id}&from=tenant&uid=${object_id}`
            });
            newClassifiedCategories[parseInt(object_id)] = classifiedCategory;
            newRoomsCategories[ReportsCategory.CENTRALIZED][room].push(`${object_id}`);
            if (!newAvailableClassifications.includes(classifiedCategory)) {
                newAvailableClassifications.push(classifiedCategory);
            }
            newCentralizedStates[`${room}-${classifiedCategory}`] = 'Attention Required';

            state.orderedData = newOrderedData;
            state.classifiedCategories = newClassifiedCategories;
            state.roomsCategories = newRoomsCategories;
            state.availableClassifications = newAvailableClassifications
            state.centralizedStates = newCentralizedStates;

            const items = (state.selectedCategories || []).reduce(
                (acc, category) => [
                    ...acc, 
                    ...((state.roomsData || []).length === 0 ? state.inventoryCategories : state.roomsCategories[state.tabView])[category]
                ], []
            );
            state.filteredData = filterData(
                state.tabView, newOrderedData, state.selectedCategories, state.checkboxStates, items, 
                state.previewMode, newCentralizedStates, state.selectedCentralizedStates
            );
        },
        updateZoomOutView: (state, action) => {
            state.zoomOutView = action.payload;
        },
        toggleExportName: (state, action) => {
            state.exportReport.exportName = action.payload;
        },
        fetchAllExportReports: (state, action) => {
            state.exportReport.allExportReports = action.payload;
        },
        setManagementData: (state, action) => {
            state.management = action.payload;
        },
        setSelectedCategories: (state, action) => {
            state.selectedCategories = action.payload;
        },
        setSelectedStates: (state, action) => {
            state.selectedCentralizedStates = action.payload;
        },
        setCurrentExportExistence: (state, action) => {
            state.exportReport.existence = action.payload;
        },
        setCurrentExportReport: (state, action) => {
            state.exportReport.currentExportReport = action.payload;
            state.includeVideoInExport = action.payload?.exported_data?.includeVideo;
            state.excludeCostFromExport = action.payload?.exported_data?.excludeCost;
            const isCentralized = action.payload?.metadata?.isCentralized === true;
            state.centralizedView = isCentralized;
            if (isCentralized) {
                state.tabView = ReportsCategory.CENTRALIZED;
            } else {
                state.tabView = ReportsCategory.INVENTORY;
            }
            state.finishExportMode = true;
        },
        bulkUpdate: (state, action) => {
            const { exportName, exportRole, exportId, editMode } = action.payload;
            if (exportName) state.exportReport.exportName = exportName;
            if (exportRole) state.exportReport.propertyRole = exportRole;
            if (exportId) {
                if (state.exportReport.currentExportReport?.export_id) {
                    state.exportReport.currentExportReport.export_id = exportId;
                } else {
                    state.exportReport.currentExportReport = {export_id: exportId};
                }
            }
            if (editMode) {
                if (editMode === EDIT_STATE.START) {
                    state.editExportMode = true;
                    state.finishExportMode = false;
                } else {
                    state.finishExportMode = true;
                }
            }
        },
        setCurrentExportId: (state, action) => {
            if (state.exportReport.currentExportReport?.export_id) {
                state.exportReport.currentExportReport.export_id = action.payload;
            } else {
                state.exportReport.currentExportReport = {export_id: action.payload};
            }
        },
        setFetchExportReportLoding: (state, action) => {
            state.fetchExportReportLoding = action.payload;
        },
        updateCentralizedShowComments: (state, action) => {
            const { k, v } = action.payload;
            state.centralizedShowComments[k] = v;
        },
        setIncludeVideoInExport: (state, action) => {
            state.includeVideoInExport = action.payload;
        },
        setExcludeCostFromExport: (state, action) => {
            state.excludeCostFromExport = action.payload;
        },
        resetCurrentExportReport: (state, action) => {
            state.exportReport.currentExportReport = null;
        },
        setSelectedCurrency: (state, action) => {
            state.exportReport.selectedCurrency = action.payload;
        },
        resetEditMode(state) {
            state.finishExportMode = false
            state.editExportMode = false;
            state.checkboxStates = {};
            state.exportReport.customText = ''; 
            state.previewMode = false;
            if (state.exportReport.currentExportReport?.export_id) {
                state.exportReport.currentExportReport.export_id = undefined;
            }
        },
        reseItemMetadata(state) {
            state.itemMetadata = {};
            state.checkboxStates = {};
            state.exportReport.customText = '';
            state.exportReport.selectedCurrency = "usd"
            state.commentModes = {}
        },
        setPreviewMode(state, action) {
            state.previewMode = action.payload;
        },
        setCustomText(state, action) {
            state.exportReport.customText = action.payload;
        },
        setLinkModal(state, action) {
            state.linkModalOpen = action.payload;
        },
        setSelectedImageIndex(state, action) {
            state.selectedImageIndex = action.payload;
        },
        setSelectedNoteIndex(state, action) {
            state.selectedNoteIndex = action.payload;
        },
        setSaveEditData(state, action) {
            state.selectedImageIndex = action.payload;
        },
        setEditExportMode(state, action) {
            state.editExportMode = action.payload;
        },
        setIsLocked(state, action) {
            state.isLocked = action.payload;
        },
        setCheckboxStates(state, action) {
            const { object_id, category, checked } = action.payload;
            const finalCategory = category || state.tabView;
            if (state.checkboxStates[finalCategory] === undefined) {
                state.checkboxStates[finalCategory] = {[object_id]: checked};
            } else {
                state.checkboxStates[finalCategory][object_id] = checked;
            }
            // state.checkboxStates[object_id] = checked;
        },
        setItemTitle(state, action) {
            const { object_id, category, title } = action.payload;
            const finalCategory = category || state.tabView;
            const m = { description: '', price: '', title: '' };
            if (!(state.itemMetadata[finalCategory] || {})[object_id]) {
                if (state.itemMetadata[finalCategory] === undefined) {
                    state.itemMetadata[finalCategory] = {[object_id]: m};
                } else {
                    state.itemMetadata[finalCategory][object_id] = m;
                }
            }
            state.itemMetadata[finalCategory][object_id].title = title;
            // if (!state.itemMetadata[object_id]) {
            //     state.itemMetadata[object_id] = { description: '', price: '', title: '' };
            // }
            // state.itemMetadata[object_id].title = title;
        },
        updateInspectionType(state, action) {
            const { inspectionType } = action.payload;
            state.inspectionType = inspectionType;
        },
        setItemDescription(state, action) {
            const { object_id, category, description } = action.payload;
            const finalCategory = category || state.tabView;
            const m = { description: '', price: '' };
            if (!(state.itemMetadata[finalCategory] || {})[object_id]) {
                if (state.itemMetadata[finalCategory] === undefined) {
                    state.itemMetadata[finalCategory] = {[object_id]: m};
                } else {
                    state.itemMetadata[finalCategory][object_id] = m;
                }
            }
            state.itemMetadata[finalCategory][object_id].description = description;
            // if (!state.itemMetadata[object_id]) {
            //     state.itemMetadata[object_id] = { description: '', price: '' };
            // }
            // state.itemMetadata[object_id].description = description;
        },
        setItemPrice(state, action) {
            const { object_id, category, price } = action.payload;
            const finalCategory = category || state.tabView;
            const m = { description: '', price: '' };
            if (!(state.itemMetadata[finalCategory] || {})[object_id]) {
                if (state.itemMetadata[finalCategory] === undefined) {
                    state.itemMetadata[finalCategory] = {[object_id]: m};
                } else {
                    state.itemMetadata[finalCategory][object_id] = m;
                }
            }
            state.itemMetadata[finalCategory][object_id].price = price;
            // if (!state.itemMetadata[object_id]) {
            //     state.itemMetadata[object_id] = { description: '', price: '' };
            // }
            // state.itemMetadata[object_id].price = price;
        },
        resert(state, action) {
            state.itemMetadata = {};
        },
        handleOpenModal(state,action){
            const index = action.payload;
            state.selectedImageIndex = index;
            state.imageViewerModal = true;
        },
        handleCloseModal(state,action) {
            state.imageViewerModal = false;
            state.selectedImageIndex = null;
        },
        handleOpenAuditoryNotesModal(state, action) {
            const { index, items } = action.payload;
            state.selectedNoteIndex = index;
            state.selectedNoteItems = items;
            state.auditoryNotesViewerModal = true;
        },
        handleCloseAuditoryNotesModal(state, action) {
            state.selectedNoteIndex = null;
            state.selectedNoteItems = [];
            state.auditoryNotesViewerModal = false;
        },
        handleOpenVideoModal(state, action) {
            const { target } = action.payload;
            state.videoPlayerModal = target;
            if (state.imageViewerModal) state.imageViewerModal = false;
        },
        handleCloseVideoModal(state, action) {
            state.videoPlayerModal = false;
            if (state.selectedImageIndex !== null) {
                state.imageViewerModal = true;
            }
        },
        handleOpenARIModal(state, action) {
            const { room } = action.payload;
            state.addReportItemModal = { room };
        },
        handleCloseARIModal(state, action) {
            state.addReportItemModal = false;
        },
        handleOpenUITModal(state, action) {
            state.editInspectionTypeModal = true;
        },
        handleCloseUITModal(state, action) {
            state.editInspectionTypeModal = false;
        },
        toggleCommentMode: (state, action) => {
            const object_id = action.payload;
            state.commentModes[object_id] = state.commentModes[object_id] === 'Edit Comment' 
                ? 'Add Comment' 
                : 'Edit Comment';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchReportsData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchReportsData.fulfilled, (state, action) => {
                state.loading = false;
                const { status, result, validatedVideoSrc, rid } = action.payload;
            
                if (status === 200 && result && result.length > 0) {
                    state.report_id = rid;
                    state.isEmptyState = false;
                    state.data = result;
                    state.header = result[0];
                    const xDate = result[1].split(" ")[0];
                    const xTime = result[1].split(" ")[1];
                    state.date = dateObjFormatter(
                        new Date(
                            "20" + xDate.split("/")[2] + "-" + xDate.split("/")[1] + "-" + 
                            xDate.split("/")[0] + "T" + xTime + "Z"
                        ), DATETIME_FORMAT
                    );
                    state.videoSrc = validatedVideoSrc;
                    state.hasBaseline = result[11] ? false : true;
                
                    let flatReportData = {};
                    if (result[11]) {
                        const {
                            inventory = [],
                            defects = []
                        } = result[5];
                        flatReportData = {
                            [ReportsCategory.INVENTORY]: inventory.map((item) => ({ ...item, label: item.label.replace("ade_", "").replace("m4_", "") })),
                            [ReportsCategory.DEFECTS]: defects,
                            inventorySize: inventory.length,
                            defectsSize: defects.length,
                        }
                    } else {
                        const { 
                            inventory = [], 
                            inventory_defects = [], 
                            organization = [], 
                            structural_defects = [] 
                        } = result[5]
                        flatReportData = {
                            [ReportsCategory.INVENTORY]: inventory.map((item) => ({ ...item, label: item.label.replace("ade_", "").replace("m4_", "") })),
                            [ReportsCategory.ORGANIZATION]: organization.map((item) => ({ ...item, label: item.label.replace("ade_", "").replace("m4_", "") })),
                            [ReportsCategory.DEFECTS]: [...inventory_defects, ...structural_defects],
                            inventorySize: inventory.length,
                            organizationSize: organization.length,
                            defectsSize: [...inventory_defects, ...structural_defects].length,
                        };
                    }
                    
                    const roomsData = result[14];
                    let roomsCategories = null;
                    const roomsList = (roomsData || []).reduce((acc1, r) => {
                        if (!acc1.includes(r.title)) acc1.push(r.title);
                        return acc1;
                    }, []);
                    const classifiedCategories = result?.[15];
                    const classifiedMetadata = result?.[16];
                    if (classifiedMetadata) {
                        state.classifiedMetadata = classifiedMetadata;
                    }
                    let tmpTabView = ReportsCategory.INVENTORY;
                    let tmpCentralizedView = false;

                    if ((roomsData || []).length > 0) {
                        roomsCategories = [
                            ReportsCategory.INVENTORY, ReportsCategory.ORGANIZATION, ReportsCategory.DEFECTS
                        ].reduce(
                            (acc1, key) => ({
                                ...acc1,
                                [key]: roomsList.reduce((acc2, r) => ({
                                    ...acc2, 
                                    [r]: (flatReportData?.[key] || []).reduce((acc3, item) => {
                                        if (item.room === r) acc3.push(item.object_id);
                                        return acc3;
                                    }, [])
                                }), {})
                            }), {}
                        );
                        roomsCategories[ReportsCategory.CENTRALIZED] = roomsList.reduce((acc1, r) => ({
                            ...acc1,
                            [r]: [ReportsCategory.INVENTORY, ReportsCategory.ORGANIZATION, ReportsCategory.DEFECTS].reduce((acc2, key) => ([
                                ...acc2, 
                                ...((flatReportData?.[key] || []).reduce((acc3, item) => {
                                    if (item.room === r) acc3.push(item.object_id);
                                    return acc3;
                                }, []))
                            ]), [])
                        }), {});
                        state.roomsCategories = roomsCategories;
                        state.roomsData = roomsData;
                        if (classifiedCategories) {
                            const availableClassifications = [...new Set(Object.values(classifiedCategories))];
                            availableClassifications.sort();
                            state.availableClassifications = availableClassifications;
                            
                            // Turn on Centralized View
                            if (!state.exportReport.currentExportReport) {
                                tmpTabView = ReportsCategory.CENTRALIZED;
                                tmpCentralizedView = true;
                            }
                        }
                        state.classifiedCategories = classifiedCategories;
                    }

                    if (state.exportReport.currentExportReport) {
                        if (state.exportReport.currentExportReport?.metadata?.isCentralized === true) {
                            tmpTabView = ReportsCategory.CENTRALIZED;
                            tmpCentralizedView = true;
                        }
                    }
                
                    state.centralizedView = tmpCentralizedView;
                    state.tabView = tmpTabView;
                    
                    state.reportData = flatReportData;
                    const orderedData = [
                        ReportsCategory.INVENTORY, ReportsCategory.ORGANIZATION, ReportsCategory.DEFECTS
                    ].reduce(
                        (acc1, key) => ({
                            ...acc1,
                            [key]: (
                                (
                                    roomsCategories === null || 
                                    (!result[11] && [ReportsCategory.INVENTORY, ReportsCategory.ORGANIZATION].includes(key))
                                ) ?
                                // key === ReportsCategory.INVENTORY ? 
                                [ReportsCategory.INVENTORY, ReportsCategory.ORGANIZATION].filter((o) => Object.keys(flatReportData).includes(o)).includes(key) ? 
                                    Object.keys(result[12]).reduce(
                                        (acc, k) => [
                                            ...acc, 
                                            ...result[12][k].map(
                                                (_id) => flatReportData[key].filter((o) => o.object_id === _id)[0]
                                            )
                                        ], []
                                    ) : flatReportData[key]
                                :
                                Object.keys(roomsCategories[key]).reduce((acc, k) => [
                                    ...acc, ...roomsCategories[key][k].map(
                                        (_id) => flatReportData[key].filter((o) => o.object_id === _id)[0]
                                    )
                                ], [])
                            )?.filter((o) => o != null)
                        }), {}
                    );
                    const centralizedStates = {};
                    orderedData[ReportsCategory.CENTRALIZED] = roomsList.reduce((acc1, r) => {
                        const roomData = (Object.keys(orderedData).reduce((acc2, key) => ([
                            ...acc2,
                            ...((orderedData?.[key] || []).reduce((acc3, item) => {
                                if (item.room === r) acc3.push(item);
                                return acc3;
                            }, []))
                        ]), []));
                        const roomIdentifications = roomData.map((item) => item.object_id);
                        const roomClassifications = [...new Set(
                            roomIdentifications.map((id) => classifiedCategories?.[id]).filter((c) => c)
                        )];
                        roomClassifications.sort();
                        return ([
                            ...acc1,
                            ...(roomClassifications.reduce((acc2, classification) => {
                                const classificationItems = roomData.map(
                                    (item) => ({...item, classifiedCategory: classifiedCategories?.[item.object_id]})
                                ).filter(
                                    (item) => item.classifiedCategory === classification
                                );
                                const hasDamages = classificationItems.some((item) => item.class >= 3000);
                                centralizedStates[`${r}-${classification}`] = hasDamages ? 'Attention Required' : 'OK';
                                
                                return ([
                                    ...acc2,
                                    ...(classificationItems)
                                ]);
                            }, []))
                        ]);
                    }, []);
                    state.orderedData = orderedData;
                    state.centralizedStates = centralizedStates;
                    state.filteredData = filterData(
                        tmpTabView , orderedData, null, 
                        state.checkboxStates, [], state.previewMode, state.centralizedStates
                    );
                    state.inspectionMediaData = result[17];
                    state.auditoryNotes = result[18];
                    state.pid = result[6];
                    state.kitchenView = result[7]['kitchen_view'];
                    state.meterview = result[7]['meter_view'];
                    state.guestData = {...defaultguestData,...result[8]};
                    state.inspectionType = result[10];
                    // API sends the opposite
                    state.inventoryCategories = result[12];
                    state.mediaData = result[13];
                } else {
                    state.isEmptyState = true;
                }
            })
            .addCase(fetchReportsData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(checkIsReportPurchased.pending, (state) => {
                state.loading = true;
            })
            .addCase(checkIsReportPurchased.fulfilled, (state, action) => {
                state.loading = false;
                const { isPurchased } = action.payload;
                state.isLocked = !isPurchased; 
            })
            .addCase(checkIsReportPurchased.rejected, (state,action) => {
                state.loading = false;
                state.isLocked = true; 
                state.error = action.payload; 
            })
            .addCase(purchaseReport.pending, (state) => {
                state.purchasedLoader = true; 
                state.purchaseStatus = null; 
                state.error = null; 
                state.receiptUrl = null; 
            })
            .addCase(purchaseReport.fulfilled, (state, action) => {
                const { status, msg, receipt_url } = action.payload;
        
                state.purchasedLoader = false;
        
                if (status === 200) {
                    state.purchaseStatus = action.payload.msg; 
                    state.receiptUrl = action.payload.receipt_url
                } else if (status === 400) {
                    state.isLocked = false; 
                    state.purchaseStatus = msg; 
                } else {
                    state.purchaseStatus = "Unexpected response from the server.";
                }
            })
            .addCase(purchaseReport.rejected, (state, action) => {
                state.purchasedLoader = false; 
                state.error = action.payload.msg || "An error occurred."; 
            });
    }
});


export const { 
    toggleEditMode, updateViewType, resetEditMode, setCheckboxStates, setSelectedImageIndex, setIncludeVideoInExport, setExcludeCostFromExport, setSelectedCategories, setSelectedStates, updateFilteredData,
    setItemTitle, setItemDescription, setItemPrice, setCustomText, handleOpenModal, handleCloseModal, setManagementData, updateCentralizedShowComments, onNewReportItem,
    setPreviewMode, setEditExportMode, setLinkModal, setIsLocked, togglePropertyRole, toggleExportName, handleOpenVideoModal, handleCloseVideoModal, updateZoomOutView,
    fetchAllExportReports, setCurrentExportReport, resetCurrentExportReport, setSelectedCurrency, handleOpenARIModal, handleCloseARIModal, selectFrameARI, setRectARI,
    reseItemMetadata, toggleCommentMode, setFetchExportReportLoding, setCurrentExportExistence, setCurrentExportId, bulkUpdate, handleOpenUITModal, handleCloseUITModal,
    updateCollapsedStates, updateInspectionType, setSelectedNoteIndex, handleOpenAuditoryNotesModal, handleCloseAuditoryNotesModal
} = reportsSlice.actions;
export default reportsSlice.reducer;